<template>
  <div>
    <div class="petition-counter">
      <h2>{{ petitionCount }}</h2>
      <h5>people want to protect Australian wildlife</h5>
      <hr />
      <p class="form-intro">
        Email your local representative asking for stronger protection of our
        wildlife now.
      </p>
    </div>

    <div v-if="isActive" class="petition-form wwf-form light">
      <div class="flip-wrapper" :class="{ 'formValidation-shake': isShake }">
        <div class="petition-form">
          <div>
            <input
              name="firstName"
              type="text"
              v-model="firstName"
              v-validate="{ required: true }"
              :class="{ 'border-red': errors.has('firstName') }"
              placeholder="First name"
              autocomplete="given-name"
            />
            <input
              name="lastName"
              type="text"
              v-model="lastName"
              v-validate="{ required: true }"
              :class="{ 'border-red': errors.has('lastName') }"
              placeholder="Last name"
              autocomplete="family-name"
            />

            <!-- EMAIL -->
            <div class="with-input-loading">
              <input
                name="email"
                type="email"
                v-model="email"
                v-validate="{
                  required: true,
                  petitionEmailIsValid: true,
                  email: true,
                }"
                :class="{ 'border-red': errors.has('email') }"
                placeholder="Email"
              />
              <div
                class="input-loading"
                v-if="emailCheckSensis.checkEmailLoading"
              >
                <img src="https://storage.googleapis.com/wwf-australia-prod.appspot.com/website-public-assets/4119/dist/assets/img/loading-tail-spin.svg" />
              </div>
              <span class="wwf-alert">
                <p v-if="errors.has('email')">{{ errors.first("email") }}</p>
              </span>
            </div>

            <!-- POST CODE -->
            <input
              name="postCode"
              type="text"
              v-model="postCode"
              placeholder="Postcode"
              v-validate="{ required: true, postcodeIsValid: true }"
              :class="{ 'border-red': errors.has('postCode') }"
              @input="updateElectorates"
            />

            <span class="wwf-alert">
              <p
                v-if="
                  errors.firstByRule('postCode', 'postcodeIsValid') ||
                  errors.firstByRule('postCode')
                "
              >
                {{ errors.first("postCode") }}
              </p>
            </span>

            <!-- PHONE NUMBER -->
            <input
              name="phoneNumber"
              type="tel"
              v-model="phoneNumber"
              placeholder="Phone Number"
              v-validate="{ required: requirePhone, petitionPhoneNumberIsValid: true }"
              :class="{ 'border-red': errors.has('phoneNumber') }"
            />

            <span class="wwf-alert">
              <p
                v-if="
                  errors.firstByRule(
                    'phoneNumber',
                    'petitionPhoneNumberIsValid'
                  )
                "
              >
                {{ errors.first("phoneNumber") }}
              </p>
            </span>

            <!-- Electorates -->
            <div class="wwf-field" v-if="isValidPostcode">
              <select required v-model="selectedElectorate">
                <option
                  v-for="electorate in electoratesList"
                  :key="electorate"
                  :value="electorate"
                >
                  {{ electorate }}
                </option>
              </select>
            </div>

            <!-- Submit btn -->
            <input
              type="button"
              class="btn-submit"
              v-on:click="save()"
              :disabled="isSaving"
              :value="isSaving ? 'Loading...' : 'Sign the petition'"
            />

            <!-- Read letter btn -->
            <input
              type="button"
              class="read-letter"
              v-on:click="openReadLetterPage"
              v-if="isCompleted"
              :disabled="isLoadingMail"
              :value="isLoadingMail ? 'Loading...' : 'View email'"
            />

            <div class="privacy-statement">
              <p>
                By signing you accept
                <a href="/privacy-policy/privacy-policy" target="_blank"
                  >WWF-Australia&rsquo;s Privacy Policy.</a
                >
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Validator } from "vee-validate";
import countTo from "vue-count-to";
import { mapState } from "vuex";
import firebase from "firebase";
import { detect } from "detect-browser";

const browser = detect();

export default {
  components: { countTo },
  name: "ThreatenedSpeciesForm",
  data() {
    return {
      petitionCount: 0,
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      postCode: "",

      viewMessage: false,
      isEmailInError: false,
      isValidPostcode: false,
      isSaving: false,
      emailCheckSensis: {
        timeout: null,
        lastTimestamp: Date.now(),
        checkEmailLoading: false,
        emailRegex: /.+\@.+\..+/,
      },

      electoratesList: [],
      selectedElectorate: "",
      MPName: "",
      speciesCount: "",

      isShake: false,

      letterContent: "",
      isLoadingMail: false,
    };
  },

  computed: {
    isCompleted() {
      return (
        this.firstName != "" &&
        this.lastName != "" &&
        this.email != "" &&
        this.postCode != "" &&
        this.isValidPostcode
      );
    },
  },
  props: {
    limit: {
      type: Number,
      default: 1000,
    },
    requirePhone:{
      type: Boolean,
      default: false,
    },
    isActive: {
      type: Boolean,
      required: true,
    },
    datalayerName: {
      type: String,
      required: true,
    },
    programName: {
      type: String,
      required: true,
    },
    productIds: {
      type: Array,
      required: false,
      default: () => [],
    },
    messageId: {
      type: String,
    },
  },

  async created() {
    firebase
      .firestore()
      .collection("campaigns")
      .doc(this.datalayerName)
      .onSnapshot(
        (snapshot) => {
          const data = snapshot.data();
          if (!!data) {
            this.petitionCount = data.count;
          }
        },
        (error) => {
          console.error(error);
        }
      );

    // Check the email
    let petitionEmailIsValid = (email) => {
      return new Promise((resolve, reject) => {
        Promise.all([this.checkEmailDuplicate(), this.checkEmail()])
          .then((results) => {
            for (const r of results) {
              if (!r.valid) {
                return resolve(r);
              }
            }
            this.errors.remove("email");
            resolve({ valid: true });
          })
          .catch((err) => {
            console.error(err);
            this.errors.remove("email");
            resolve({ valid: true });
          });
      });
    };

    Validator.extend("petitionEmailIsValid", {
      validate: petitionEmailIsValid,
      getMessage: (field, params, data) => data.message,
    });

    // Check postcode
    Validator.extend("postcodeIsValid", {
      validate: (postcode) => {
        if (this.isValidPostcode) {
          return { valid: true };
        } else {
          return {
            valid: false,
            data: { message: "Invalid postcode" },
          };
        }
      },
      getMessage: (field, params, data) => data.message,
    });

    // Check Phone Number
    let petitionPhoneNumberIsValid = (phoneNumber) => {
      const validationRegex = /^(\+?61|0)([0-9]{8,9})/;
      if (!phoneNumber.replace(/ /g, "").match(validationRegex)) {
        return {
          valid: false,
          data: { message: "Invalid phone number" },
        };
      } else {
        return {
          valid: true,
        };
      }
    };

    Validator.extend("petitionPhoneNumberIsValid", {
      validate: petitionPhoneNumberIsValid,
      getMessage: (field, params, data) => data.message,
    });
  },

  mounted: function () {
    // Fetch the content of the messages
    if (this.messageId) {
      this.$store
        .dispatch("getContentMessageById", this.messageId)
        .then((value) => {
          console.log("value", value);
          if (value) {
            this.letterContent = value.content;
          } else {
            this.letterContent = "";
          }
        });
    }
  },
  methods: {
    async updateElectorates(event) {
      const newPostcode = event.target.value;
      try {
        if (newPostcode.length === 4) {
          // Get electorates from postcode
          const electorates = (
            await this.$store.dispatch("getElectorates", newPostcode)
          ).map((e) => e.electoraldivisionname);

          // Handle postcode not found
          if (electorates.length !== 0) {
            this.isValidPostcode = true;
            this.electoratesList = electorates;
            this.selectedElectorate = electorates[0];
          }
        } else {
          this.isValidPostcode = false;
          this.selectedElectorate = "";
          this.electoratesList = [];
        }
      } catch (err) {
        console.error(err);
      }
    },
    async fetchMailData() {
      this.isLoadingMail = true;
      if (this.selectedElectorate) {
        // Get MP
        const mp = await this.$store.dispatch("getMp", this.selectedElectorate);
        if (mp) {
          this.MPName = mp.name;

          // Find species count
          const [electorateData, heroSpecies] = await Promise.all([
            this.$store.dispatch("getElectoratesGrade", [
              this.selectedElectorate,
            ]),
            this.$store.dispatch("getHeroSpecies", this.selectedElectorate),
          ]);

          this.speciesCount =
            electorateData[0].numberofthreatenedspeciesinelectorate;
        }

        this.isLoadingMail = false;
      }
    },
    checkEmailDuplicate: function () {
      return new Promise((resolve, reject) => {
        this.$store
          .dispatch("checkEmail", {
            campaignId: this.datalayerName,
            email: this.email,
          })
          .then((result) => {
            if (result) {
              resolve({
                valid: false,
                field: "email",
                data: { message: "Your email has already been submitted" },
              });
            } else {
              resolve({ valid: true });
            }
          });
      });
    },
    checkEmail: function () {
      return new Promise((resolve, reject) => {
        let timestamp = Date.now(); // timestamp for this api call
        this.emailCheckSensis.lastTimestamp = timestamp; // 1 , 2 , 3
        this.emailCheckSensis.checkEmailLoading = true;
        // Fetch Suggestions
        this.$store
          .dispatch("validateEmail", this.email)
          .then((response) => {
            if (response && timestamp >= this.emailCheckSensis.lastTimestamp) {
              this.emailCheckSensis.checkEmailLoading = false;
              resolve(response);
            }
          })
          .catch((err) => {
            console.error(err);
            this.emailCheckSensis.checkEmailLoading = false;
            // If it fails, let the user free to continue
            resolve({ valid: true });
          });
      });
    },
    async openReadLetterPage() {
      // Get the content of the electorate
      await this.fetchMailData();

      // Parse the variables in the letter contents
      const parsedLetter = this.letterContent
        .replace(/{{data-name}}/gi, `${this.firstName} ${this.lastName}`)
        .replace(/{{data-postcode}}/gi, this.postCode)
        .replace(/{{data-email}}/gi, this.email)
        .replace(/{{data-electorateName}}/gi, this.selectedElectorate)
        .replace(/{{data-MPName}}/gi, this.MPName)
        .replace(/{{data-speciesCount}}/gi, this.speciesCount);

      // Set up the letter content
      this.$store.commit("setPopupContent", {
        title: `${this.MPName}, Please Help Save Our Threatened Wildlife`,
        message: parsedLetter,
        shareButton: false,
        recommendedReading: false,
        component: null,
        componentparams: null,
      });
      this.$store.commit("openPetitionMessagePopup", true);
    },
    save() {
      // Exit the function if it's busy
      if (this.isSaving) {
        return;
      }
      this.isSaving = true;

      // Validate data
      this.$validator.validate().then(async (result) => {
        // Send data
        if (result) {
          await this.saveLead();
          this.firstName = "";
          this.lastName = "";
          this.phoneNumber = "";
          this.email = "";
          this.postCode = "";
          this.selectedElectorate = "";
          this.isValidPostcode = false;
          this.isSaving = false;
        } else {
          console.log("form invalid");
          this.isSaving = false;
          this.isShake = true;
          setTimeout(
            function () {
              this.isShake = false;
            }.bind(this),
            500
          );
        }
      });
    },
    async saveLead() {
      try {
        const postBody = {
          firstName: this.firstName,
          lastName: this.lastName,
          phone: this.phoneNumber,
          email: this.email,
          postcode: this.postCode,
          electorate: this.selectedElectorate,
          programName: this.programName,
          cookieID: this.$cookie.get("_mkto_trk"),
          keepData: true,
          ssoId: this.$store.getters.ssoId,
        };

        if (browser) {
          postBody.browsername = browser.name;
          postBody.browserversion = browser.version;
          postBody.browseros = browser.os;
        }

        // Get electorate details
        const mp = await this.$store.dispatch("getMp", this.selectedElectorate);
        if (mp) {
          postBody.mpName = mp.name;
          postBody.mpEmail =
            mp.email && mp.email.length > 0 ? mp.email : mp.manualEmail;

          // Find species count
          const [electorateData, heroSpecies] = await Promise.all([
            this.$store.dispatch("getElectoratesGrade", [
              this.selectedElectorate,
            ]),
            this.$store.dispatch("getHeroSpecies", this.selectedElectorate),
          ]);

          postBody.specieCount =
            electorateData[0].numberofthreatenedspeciesinelectorate;
          postBody.specieName = heroSpecies.commonName;
        }

        console.log("postBody", postBody);

        // Save petition postBody
        await this.$store.dispatch("saveLead", {
          campaignId: this.datalayerName,
          lead: postBody,
        });

        // Open popup
        this.openThankYouPopup();
      } catch (err) {
        console.log(err);
        console.error("Petition submit failed");
        this.isSaving = false;
      }
    },
    openThankYouPopup() {
      const id = "my-backyard-stepup";
      this.$store.dispatch("getContentMessageById", id).then((response) => {
        if (!!response) {
          this.$store.commit("setPopupContent", {
            component: "threatened-species-thank-you-popup",
            componentparams: {
              title: response.description,
              content: response.content,
              isAmplitude: false
            },
          });
          this.$store.commit("openCustomContentPopup", true);
        }
      });
    },
  },
};
</script>

<style>
.petition-progressbar + .petition-form {
  position: relative;
  top: -90px;
}
</style>

<style lang="css" scoped>
.petition-header,
.petition-counter {
  margin: -350px -20px 20px;
  padding: 20px 20px 0px;
  border-bottom: 1px solid var(--wwfGreyDE);
  background-color: var(--wwfBrandBlack);
}

.petition-header {
  display: flex;
  flex-direction: column;
  margin-bottom: -20px;
  padding: 40px;
}

.petition-header,
.petition-counter h2,
h5,
p {
  color: var(--wwfWhite);
}

.petition-counter h5 {
  margin-top: 20px;
}

.petition-header h4 {
  margin-top: 14px;
  font-size: 32px;
  font-family: "wwfRegular";
  font-weight: 400;
  line-height: 32px;
}

.petition-counter hr {
  border: 1px solid var(--wwfWhite);
  opacity: 0.4;
}

.petition-header-img {
  width: 75px;
  height: 75px;
}

.petition-header-img img {
  width: 100%;
}

.petition-header ul {
  margin-top: 12px;
  font-size: 18px;
}

.petition-header ul li {
  margin-top: 16px;
  font-size: 18px;
}

.petition-header ul li:nth-child(1) {
  margin-top: 0px;
}

.petition-header button {
  margin-top: 28px;
  padding: 12px;
  color: var(--wwfWhite);
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background-color: var(--wwfOrange);
  cursor: pointer;
}

.petition-header hr {
  width: 100%;
  opacity: 0.2;
}

.petition-thank-you-header {
  border-top: 6px solid var(--wwfOrange);
}

span.wwf-alert {
  position: absolute;
  display: block;
  text-align: left;
  width: 100%;
  pointer-events: none;
  z-index: 9;
}

span.wwf-alert p {
  position: relative;
  background-color: var(--errorColor);
  left: 0;
  top: -18px;
  display: inline-block;
  margin: 0 auto;
  text-align: center;
  padding: 5px 10px;
  line-height: normal;
  border-radius: 3px;
  color: var(--wwfWhite);
  font-size: 13px;
  box-shadow: 0 3px 13px var(--wwfTransparentBlack020);
  z-index: 10;
  font-weight: 600;
}

.btn-submit:disabled {
  background-color: rgba(0, 0, 0, 0.2) !important;
}

.input-loading {
  top: 6px;
}

.petition-form.wwf-form input[type="button"]:hover {
  background-color: var(--wwfDarkBlue);
  color: var(--wwfWhite);
}

.petition-form.wwf-form input.read-letter {
  background-color: var(--wwfWhite);
  font-family: "Lato", sans-serif;
  color: var(--wwfBlue);
  mix-blend-mode: normal;
  border: 1px solid var(--wwfBlue);
  box-sizing: border-box;
  border-radius: 3px;
}

/* ORANGE THEME */
.orange .petition-counter {
  background-color: var(--wwfBrandBlack);
}

.orange .petition-form.wwf-form .read-letter {
  color: var(--wwfOrange);
  background-color: var(--wwfWhite);
  border: 1px solid var(--wwfOrange);
}

.orange .petition-form.wwf-form .btn-submit {
  background-color: var(--wwfOrange);
}

.orange .petition-form.wwf-form input[type="button"]:hover {
  background-color: var(--wwfDarkOrange);
  color: var(--wwfWhite);
}

/* BLUE THEME */
.blue .petition-counter {
  background-color: var(--wwfBlue);
}

/* GREEN THEME */
.green .petition-counter {
  background-color: var(--wwfGreen);
}

/* INVERTED BLUE THEME */
.inverted-blue .petition-counter {
  background-color: var(--wwfWhite);
}

.inverted-blue .petition-counter h2,
.inverted-blue .petition-counter h5,
.inverted-blue .petition-counter p {
  color: var(--wwfBlue);
}

.inverted-blue .petition-counter hr {
  border: 1px solid var(--wwfBlue);
}

.inverted-blue .petition-form.wwf-form .read-letter {
  color: var(--wwfBlue);
  background-color: var(--wwfWhite);
  border: 1px solid var(--wwfBlue);
}

.inverted-blue .petition-form.wwf-form .btn-submit {
  background-color: var(--wwfBlue);
}
</style>



// WEBPACK FOOTER //
// ThreatenedSpeciesForm.vue?9aceaad8